import { FlexColumn, SpaceBetween, TextBody } from "@opensea/ui-kit"
import type { ReactNode } from "react"
import { useTranslations } from "use-intl"
import { Button } from "../../../components/Button"
import { EMBEDDED_WALLET_CONNECTOR_ID } from "../../../lib/wagmi"
import { useAccount } from "../../../polychain/hooks"
import { useErrors } from "../../hooks/useErrors"
import { ConnectEmbeddedWallet } from "../ConnectEmbeddedWallet"
import type { PaymentMethod } from "../types"
import { WalletPaymentStep } from "../WalletPaymentStep"

type Props = {
  cardDisabled: boolean
  paymentMethod: PaymentMethod | undefined
  onChangePaymentMethod?: () => void
  timeline: ReactNode
  setPaymentMethod: (paymentMethod: PaymentMethod | undefined) => void
  currencySelector: ReactNode
  hidePaymentMethodOptions?: boolean
}

export const CheckoutPaymentStep = ({
  cardDisabled,
  paymentMethod,
  onChangePaymentMethod,
  timeline,
  setPaymentMethod,
  currencySelector,
  hidePaymentMethodOptions,
}: Props) => {
  const t = useTranslations("wallet.CheckoutPaymentStep")
  const isWalletEmbeddedSelected =
    paymentMethod === `wallet-${EMBEDDED_WALLET_CONNECTOR_ID}`
  const { connectorId } = useAccount()
  const isEmbeddedWalletConnected = connectorId === EMBEDDED_WALLET_CONNECTOR_ID
  const { clearErrors } = useErrors()

  const handleChangePaymentMethod = () => {
    onChangePaymentMethod?.()
    clearErrors()
  }

  if (isWalletEmbeddedSelected && !isEmbeddedWalletConnected) {
    return (
      <FlexColumn className="w-full min-w-0">
        <ConnectEmbeddedWallet
          onChangePaymentMethod={
            onChangePaymentMethod ? handleChangePaymentMethod : undefined
          }
        />
      </FlexColumn>
    )
  }

  return (
    <>
      {onChangePaymentMethod && (
        <SpaceBetween className="mb-4 items-center">
          <TextBody
            className="uppercase"
            color="text-secondary"
            size="xs"
            weight="semibold"
          >
            {t("payment")}
          </TextBody>
          <Button
            onClick={handleChangePaymentMethod}
            size="sm"
            variant="secondary"
          >
            {t("change")}
          </Button>
        </SpaceBetween>
      )}
      <FlexColumn className="w-full min-w-0">
        <WalletPaymentStep
          cardDisabled={cardDisabled}
          currencySelector={currencySelector}
          hidePaymentMethodOptions={hidePaymentMethodOptions}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          timeline={timeline}
        />
      </FlexColumn>
    </>
  )
}
